import { useState } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { motion } from 'motion/react'
import { Eye, EyeOff, Loader2 } from 'lucide-react'
import { Capacitor } from '@capacitor/core'
import { useAuth } from './AuthContext'

export default function Login() {
  const { t } = useTranslation('auth')
  const { signIn, resetPassword, signInWithGoogle, signInWithApple } = useAuth()
  const isDevServer = import.meta.env.DEV && !Capacitor.isNativePlatform() && (location.hostname === 'localhost' || location.hostname === '127.0.0.1')
  const [email, setEmail] = useState(isDevServer ? 'alice@example.com' : '')
  const [password, setPassword] = useState(isDevServer ? 'password123' : '')
  const [showPassword, setShowPassword] = useState(false)
  const [isForgotPassword, setIsForgotPassword] = useState(false)
  const [resetSent, setResetSent] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSocialSignIn = async (provider: 'apple' | 'google') => {
    setError(null)
    setLoading(true)
    try {
      const { error: authError } = provider === 'apple'
        ? await signInWithApple()
        : await signInWithGoogle()
      if (authError) setError(authError)
    } catch {
      setError(t('connectionError'))
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      if (isForgotPassword) {
        const { error: resetError } = await resetPassword(email)
        if (resetError) {
          setError(resetError)
        } else {
          setResetSent(true)
        }
        return
      }

      const { error: authError } = await signIn(email, password)

      if (authError) {
        setError(authError)
      }
    } catch {
      setError(t('connectionError'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-full px-8 pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-sm"
      >
        {/* Logo / Brand */}
        <div className="text-center mb-10">
          <img src="/tendbot.png" alt="TendBot" className="h-20 w-auto mx-auto mb-3" />
          <h1 className="font-display text-4xl text-gold font-semibold mb-2">TendBot</h1>
          <p className="text-pearl/50 text-sm">{t('tagline')}</p>
        </div>

        {/* Login Card */}
        <div className="glass-card rounded-2xl p-6 glow-gold">
          <h2 className="font-display text-xl text-ivory font-semibold mb-6 text-center">
            {isForgotPassword ? t('resetPassword') : t('welcomeBack')}
          </h2>

          {resetSent ? (
            <div className="text-center py-4">
              <p className="text-ivory text-base mb-3">{t('checkEmail')}</p>
              <p className="text-pearl/50 text-sm leading-relaxed mb-6">
                <Trans i18nKey="resetLinkSent" ns="auth" values={{ email }} components={{ 1: <strong className="text-ivory" /> }} />
              </p>
              <button
                onClick={() => { setIsForgotPassword(false); setResetSent(false); setError(null) }}
                className="text-gold/60 hover:text-gold text-sm transition-colors"
              >
                {t('backToSignIn')}
              </button>
            </div>
          ) : (
            <>
              {!isForgotPassword && (
                <>
                  <div className="space-y-3">
                  <button
                    type="button"
                    onClick={() => handleSocialSignIn('apple')}
                    disabled={loading}
                    className="w-full bg-black text-white font-medium py-3 rounded-xl transition-colors flex items-center justify-center gap-3 border border-white/10 hover:bg-black/80 disabled:opacity-50"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.52-3.23 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" fill="white"/>
                    </svg>
                    {t('signInWithApple')}
                  </button>

                  <button
                    type="button"
                    onClick={() => handleSocialSignIn('google')}
                    disabled={loading}
                    className="w-full bg-slate-dark/80 border border-gold/10 hover:border-gold/30 text-ivory font-medium py-3 rounded-xl transition-colors flex items-center justify-center gap-3 disabled:opacity-50"
                  >
                    <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
                      <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
                      <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
                      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 2.58 9 3.58z" fill="#EA4335"/>
                    </svg>
                    {t('signInWithGoogle')}
                  </button>
                  </div>

                  <div className="flex items-center gap-3 my-5">
                    <div className="flex-1 h-px bg-gold/10" />
                    <span className="text-pearl/60 text-xs">{t('orDivider')}</span>
                    <div className="flex-1 h-px bg-gold/10" />
                  </div>
                </>
              )}

              {error && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-rose text-xs text-center py-1 mb-2"
                >
                  {error}
                </motion.p>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-pearl/60 text-xs mb-1.5 ml-1">{t('emailLabel')}</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full bg-slate-dark/80 border border-gold/10 rounded-xl px-4 py-3 text-ivory text-sm placeholder:text-pearl/55 focus:outline-none focus:border-gold/40 transition-colors"
                    placeholder={t('emailPlaceholder')}
                  />
                </div>

                {!isForgotPassword && (
                  <div>
                    <label className="block text-pearl/60 text-xs mb-1.5 ml-1">{t('passwordLabel')}</label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        minLength={6}
                        className="w-full bg-slate-dark/80 border border-gold/10 rounded-xl pl-4 pr-11 py-3 text-ivory text-sm placeholder:text-pearl/55 focus:outline-none focus:border-gold/40 transition-colors"
                        placeholder={t('passwordPlaceholder')}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        aria-label={showPassword ? t('hidePassword') : t('showPassword')}
                        className="absolute inset-y-0 right-0 flex items-center px-3 text-pearl/55 hover:text-ivory transition-colors"
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gold hover:bg-gold-light text-midnight font-semibold py-3 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading && <Loader2 size={16} className="animate-spin" />}
                  {isForgotPassword ? t('sendResetLink') : t('signInButton')}
                </button>
              </form>

              <div className="mt-5 text-center space-y-2">
                {!isForgotPassword && (
                  <button
                    onClick={() => { setIsForgotPassword(true); setError(null) }}
                    className="block w-full text-pearl/60 hover:text-pearl/80 text-xs transition-colors"
                  >
                    {t('forgotPassword')}
                  </button>
                )}
                {isForgotPassword && (
                  <button
                    onClick={() => { setIsForgotPassword(false); setError(null) }}
                    className="text-gold/60 hover:text-gold text-xs transition-colors"
                  >
                    {t('backToSignIn')}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </motion.div>
    </div>
  )
}
